import { forwardRef } from "react";

import {
  FormControl,
  FormHelperText,
  InputLabel,
  OutlinedInput,
} from "@mui/material";
import PropTypes from "prop-types";

export const CustomInputField = forwardRef(
  (
    {
      id,
      label,
      name,
      register = (name) => ({ name }),
      value,
      onChange,
      onBlur,
      disabled,
      fullWidth = true,
      InputLabelProps,
      InputProps,
      inputProps,
      // MUI 9 slot API (e.g. Autocomplete's renderInput params). Map its
      // input/htmlInput/inputLabel slots onto our OutlinedInput so the
      // Autocomplete input ref + adornments wire up, and consume slotProps/size
      // here so they don't leak onto the FormControl DOM node.
      slotProps,
      size,
      helperText,
      FormHelperTextProps,
      sx,
      // OutlinedInput specific props
      multiline,
      rows,
      error,
      required,
      placeholder,
      ...props
    },
    ref
  ) => {
  // Get register props if register function is provided
  const registerProps = name && register ? register(name) : {};

  // Determine if we're using register pattern or explicit props
  const isUsingRegister = name && register && !value && !onChange;

  // Only the conflicting props - let register handle them or use explicit props
  const conditionalProps = isUsingRegister
    ? registerProps
    : {
        name,
        value: value ?? "",
        onChange,
        onBlur,
      };

  // Merge MUI 9 slot props with the legacy explicit props (explicit wins).
  const mergedInputLabelProps = { ...slotProps?.inputLabel, ...InputLabelProps };
  const mergedInputProps = { ...slotProps?.input, ...InputProps };
  const mergedHtmlInputProps = { ...slotProps?.htmlInput, ...inputProps };

  return (
    <FormControl
      fullWidth={fullWidth}
      variant="standard"
      sx={{ ...(label && { pt: 3.5 }), ...sx }}
      disabled={disabled}
      error={error}
      {...props}
    >
      {label && (
        <InputLabel
          shrink
          disabled={disabled}
          required={required}
          sx={{
            fontSize: "0.875rem",
            lineHeight: "1.125rem",
            transform: "scale(1)",
          }}
          {...{ htmlFor: id }}
          {...mergedInputLabelProps}
        >
          {label}
        </InputLabel>
      )}
      <OutlinedInput
        ref={ref}
        id={id}
        size={size}
        required={required}
        disabled={disabled}
        multiline={multiline}
        rows={rows}
        placeholder={placeholder}
        {...conditionalProps}
        {...mergedInputProps}
        inputProps={mergedHtmlInputProps}
      />
      {helperText && (
        <FormHelperText {...FormHelperTextProps}>{helperText}</FormHelperText>
      )}
    </FormControl>
    );
  }
);

CustomInputField.displayName = "CustomInputField";

CustomInputField.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string,
  register: PropTypes.func,
  value: PropTypes.any,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  InputLabelProps: PropTypes.object,
  InputProps: PropTypes.object,
  inputProps: PropTypes.object,
  slotProps: PropTypes.object,
  size: PropTypes.string,
  helperText: PropTypes.string,
  FormHelperTextProps: PropTypes.object,
  sx: PropTypes.object,
  multiline: PropTypes.bool,
  rows: PropTypes.number,
  error: PropTypes.bool,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
};
